/**
 * Payment previews for the Decisions forms (ported from nextjs-app `lib/math/amortization.ts`).
 * This is the one piece of loan arithmetic the client does: it shows the user what a payment would
 * be while they fill in a form. The plan itself always comes from the engine.
 *
 * Canadian fixed-rate mortgages compound semi-annually, not in advance (Interest Act s.6), so a
 * mortgage's effective monthly rate is (1 + r/2)^(1/6) − 1. Other loans compound monthly.
 */
export type Compounding = "monthly" | "semiAnnual";

/** The effective monthly rate for an annual rate in percent. */
export function monthlyRate(aprPercent: number, compounding: Compounding = "monthly"): number {
  const r = Math.max(0, Number(aprPercent) || 0) / 100;
  if (r <= 0) return 0;
  return compounding === "semiAnnual" ? Math.pow(1 + r / 2, 1 / 6) - 1 : r / 12;
}

/** Level monthly payment that pays `principal` off over `termMonths`. */
export function monthlyPayment(
  principal: number,
  aprPercent: number,
  termMonths: number,
  compounding: Compounding = "monthly",
): number {
  const p = Math.max(0, Number(principal) || 0);
  const n = Math.max(1, Math.floor(Number(termMonths) || 0));
  const i = monthlyRate(aprPercent, compounding);
  if (i <= 0) return p / n;
  return (i * p) / (1 - Math.pow(1 + i, -n));
}

/** A Canadian mortgage payment (semi-annual compounding) over an amortization in years. */
export function mortgagePayment(principal: number, ratePercent: number, years: number): number {
  return monthlyPayment(principal, ratePercent, Math.round(years * 12), "semiAnnual");
}

/** Total paid and interest over the term, for the "what financing costs" preview. */
export function financingCost(
  principal: number,
  aprPercent: number,
  termMonths: number,
  compounding: Compounding = "monthly",
): { payment: number; totalPaid: number; interest: number } {
  const payment = monthlyPayment(principal, aprPercent, termMonths, compounding);
  const n = Math.max(1, Math.floor(Number(termMonths) || 0));
  const totalPaid = payment * n;
  return { payment, totalPaid, interest: Math.max(0, totalPaid - Math.max(0, principal)) };
}
